/**
 * Workplan Builder - Multi-step wizard for workplan generation
 * Uses the report job infrastructure (generic, not workplan-specific)
 * Scalable, production-ready, follows ProposalBuilder pattern exactly
 */

import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Input, Select, ReportJobMonitor } from '@tracekit/core';
import { FileText, Plus, Trash2, AlertCircle, Loader2, Search, Brain, Zap, StopCircle, CheckCircle, ChevronRight, ChevronDown } from 'lucide-react';
import { useWorkspace, getAPIClient } from '@tracekit/core';
import { useWorkplanGeneration } from '../contexts/WorkplanGenerationContext';
import { useToastStore } from '@tracekit/core';
import { Badge } from '@tracekit/core';
import { useWorkplanJobSubmit } from '@tracekit/core';
import { WorkplanTemplateImportModal } from './WorkplanTemplateImportModal';

interface WorkplanBuilderProps {
  pastProjectId?: string; // Optional - not needed for template-based generation
  pastProjectName: string;
  clientName: string;
  serviceLine?: string;
  initialTemplate?: WorkplanTemplate; // Optional - for policy pack templates
  onComplete?: (jobId: string) => void;
  onCancel?: () => void;
}

type TemplateMethod = 'import_template' | 'from_past_workplan';

interface WorkplanTemplate {
  sections: Array<{ name: string; description: string }>;
  default_questions: Array<{ section: string; question: string }>;
  template_source?: string;
  service_line?: string;
}

export function WorkplanBuilder({
  pastProjectId,
  pastProjectName,
  clientName,
  serviceLine,
  initialTemplate,
  onComplete,
  onCancel,
}: WorkplanBuilderProps) {
  const { projectId } = useWorkspace();
  const { addToast } = useToastStore();
  const { setSelectedProject } = useWorkplanGeneration();
  const submitMutation = useWorkplanJobSubmit();
  
  const [step, setStep] = useState(0); // Step 0: Method selection
  const [selectedMethod, setSelectedMethod] = useState<TemplateMethod | null>(null);
  const [showTemplateImport, setShowTemplateImport] = useState(false);
  const [questions, setQuestions] = useState<Array<{ section: string; question: string }>>([]);
  const [workplanName, setWorkplanName] = useState(pastProjectName || '');
  const [submittedJobId, setSubmittedJobId] = useState<string | null>(null);
  const [template, setTemplate] = useState<WorkplanTemplate | null>(null);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);
  const [importedTemplate, setImportedTemplate] = useState<WorkplanTemplate | null>(null);
  const [templateSource, setTemplateSource] = useState<string>('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamStage, setStreamStage] = useState<string>('');
  const [streamMessage, setStreamMessage] = useState<string>('');
  const [streamProgress, setStreamProgress] = useState(0);
  const [streamElapsed, setStreamElapsed] = useState(0);
  const streamStartTimeRef = useRef<number>(0);
  const streamIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);

  // Set selected project in context
  useEffect(() => {
    if (pastProjectId) {
      setSelectedProject(pastProjectId);
    }
  }, [pastProjectId, setSelectedProject]);

  // Initialize questions from template or initialTemplate
  useEffect(() => {
    if (initialTemplate && questions.length === 0) {
      // Pre-fill from policy pack template
      setImportedTemplate(initialTemplate);
      setQuestions(initialTemplate.default_questions || []);
      setTemplateSource('policy_pack_template');
      setStep(1); // Skip method selection, go directly to question editing
    } else {
      const finalTemplate = importedTemplate || template;
      if (finalTemplate && questions.length === 0 && !importedTemplate) {
        const templateQuestions = finalTemplate.default_questions || [];
        setQuestions(templateQuestions);
      }
    }
  }, [template, importedTemplate, initialTemplate, questions.length]);

  // Handle method selection
  const handleMethodSelect = (method: TemplateMethod) => {
    setSelectedMethod(method);
    
    if (method === 'import_template') {
      setShowTemplateImport(true);
    } else if (method === 'from_past_workplan') {
      // Start AI-derived template extraction
      setStep(1);
      fetchWorkplanTemplate();
    } else if (method === 'from_past_workplan') {
      setShowTemplateImport(true);
    }
  };

  // Fetch workplan template from past project
  const fetchWorkplanTemplate = async () => {
    if (!projectId) {
      addToast({
        type: 'error',
        title: 'Missing Information',
        message: 'Project ID is required',
      });
      return;
    }
    
    if (!pastProjectId) {
      addToast({
        type: 'error',
        title: 'Missing Information',
        message: 'Past Project ID is required for AI-derived templates',
      });
      return;
    }
    
    setTemplateLoading(true);
    setTemplateError(null);
    setIsStreaming(true);
    setStreamStage('retrieval');
    setStreamMessage('Searching through past workplans to extract template structure...');
    streamStartTimeRef.current = Date.now();
    
    // Start elapsed time counter
    streamIntervalRef.current = setInterval(() => {
      setStreamElapsed((Date.now() - streamStartTimeRef.current) / 1000);
    }, 100);

    try {
      const api = getAPIClient();
      
      // Use streaming endpoint if available, otherwise use regular endpoint
      const useStreamingEndpoint = true; // Always try streaming first
      
      if (useStreamingEndpoint) {
        // Try streaming endpoint
        try {
          await fetchTemplateStream(api);
        } catch (streamError) {
          console.warn('[WorkplanBuilder] Streaming failed, falling back to regular endpoint:', streamError);
          await fetchTemplateRegular(api);
        }
      } else {
        await fetchTemplateRegular(api);
      }
    } catch (error: any) {
      console.error('[WorkplanBuilder] Template extraction failed:', error);
      setTemplateError(error.response?.data?.detail || error.message || 'Failed to extract template');
      setTemplateLoading(false);
      setIsStreaming(false);
      if (streamIntervalRef.current) {
        clearInterval(streamIntervalRef.current);
        streamIntervalRef.current = null;
      }
    }
  };

  // Fetch template using streaming endpoint
  const fetchTemplateStream = async (api: any) => {
    return new Promise<void>((resolve, reject) => {
      const token = localStorage.getItem('auth_token') || '';
      const url = new URL(`${api.defaults.baseURL || ''}/api/past-projects/${pastProjectId}/workplan-template/stream`);
      url.searchParams.set('project_id', projectId!);
      url.searchParams.set('token', token);
      if (serviceLine) {
        url.searchParams.set('service_line', serviceLine);
      }

      const eventSource = new EventSource(url.toString());
      eventSourceRef.current = eventSource;

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'status') {
            setStreamStage(data.stage || 'processing');
            setStreamMessage(data.message || 'Processing...');
            setStreamProgress(data.progress || 0);
          } else if (data.type === 'complete') {
            const templateData = data.template || data;
            const finalTemplate: WorkplanTemplate = {
              sections: templateData.sections || [],
              default_questions: templateData.default_questions || [],
              template_source: templateData.template_source || 'past_workplans',
              service_line: templateData.service_line || serviceLine,
            };
            
            setTemplate(finalTemplate);
            setQuestions(finalTemplate.default_questions || []);
            setTemplateSource(finalTemplate.template_source || 'past_workplans');
            setTemplateLoading(false);
            setIsStreaming(false);
            setStreamStage('complete');
            setStreamMessage('Template extracted successfully!');
            setStreamProgress(100);
            
            if (streamIntervalRef.current) {
              clearInterval(streamIntervalRef.current);
              streamIntervalRef.current = null;
            }
            
            eventSource.close();
            eventSourceRef.current = null;
            resolve();
          } else if (data.type === 'error') {
            const errorMsg = data.message || 'Template extraction failed';
            setTemplateError(errorMsg);
            setTemplateLoading(false);
            setIsStreaming(false);
            
            if (streamIntervalRef.current) {
              clearInterval(streamIntervalRef.current);
              streamIntervalRef.current = null;
            }
            
            eventSource.close();
            eventSourceRef.current = null;
            reject(new Error(errorMsg));
          }
        } catch (parseError) {
          console.error('[WorkplanBuilder] Failed to parse SSE event:', parseError);
        }
      };

      eventSource.onerror = (error) => {
        console.error('[WorkplanBuilder] SSE error:', error);
        eventSource.close();
        eventSourceRef.current = null;
        reject(new Error('Streaming connection failed'));
      };

      // Timeout after 60 seconds
      setTimeout(() => {
        if (eventSource.readyState !== EventSource.CLOSED) {
          eventSource.close();
          eventSourceRef.current = null;
          reject(new Error('Template extraction timeout'));
        }
      }, 60000);
    });
  };

  // Fetch template using regular (non-streaming) endpoint
  const fetchTemplateRegular = async (api: any) => {
    const response = await api.post(
      `/api/past-projects/${pastProjectId}/workplan-template?project_id=${projectId}`,
      {
        project_id: projectId,
        service_line: serviceLine || null,
        adapt_questions: true,
      }
    );

    const templateData = response.data || response;
    const finalTemplate: WorkplanTemplate = {
      sections: templateData.sections || [],
      default_questions: templateData.default_questions || [],
      template_source: templateData.template_source || 'past_workplans',
      service_line: templateData.service_line || serviceLine,
    };

    setTemplate(finalTemplate);
    setQuestions(finalTemplate.default_questions || []);
    setTemplateSource(finalTemplate.template_source || 'past_workplans');
    setTemplateLoading(false);
    setIsStreaming(false);
    setStreamStage('complete');
    setStreamProgress(100);
    
    if (streamIntervalRef.current) {
      clearInterval(streamIntervalRef.current);
      streamIntervalRef.current = null;
    }
  };

  // Handle template import
  const handleTemplateImport = (imported: WorkplanTemplate) => {
    setImportedTemplate(imported);
    setQuestions(imported.default_questions || []);
    setTemplateSource(imported.template_source || 'previous_workplan_import');
    setShowTemplateImport(false);
    setStep(1);
  };

  const handleAddQuestion = () => {
    setQuestions([...questions, { section: '', question: '' }]);
  };

  const handleRemoveQuestion = (index: number) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const handleQuestionChange = (index: number, field: 'section' | 'question', value: string) => {
    const newQuestions = [...questions];
    newQuestions[index][field] = value;
    setQuestions(newQuestions);
  };

  // Handle workplan generation submission
  const handleSubmit = async () => {
    if (!projectId) {
      addToast({
        type: 'error',
        title: 'Project Required',
        message: 'Please select a project to generate workplan',
      });
      return;
    }

    if (questions.length === 0) {
      addToast({
        type: 'warning',
        title: 'Questions Required',
        message: 'Please add at least one question to generate the workplan',
      });
      return;
    }

    try {
      const result = await submitMutation.mutateAsync({
        pastProjectId,
        projectId,
        clientName: clientName || 'Client',
        questions,
        metadata: {
          workplan_name: workplanName || pastProjectName,
          service_line: serviceLine,
          template_source: templateSource,
          include_past_workplans: true,
          include_evidence: true,
        },
      });

      setSubmittedJobId(result.id);
      setStep(3); // Move to monitoring step
    } catch (error: any) {
      console.error('[WorkplanBuilder] Workplan generation failed:', error);
      // Error toast is handled by the mutation hook
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (streamIntervalRef.current) {
        clearInterval(streamIntervalRef.current);
        streamIntervalRef.current = null;
      }
    };
  }, []);

  if (!projectId) {
    return (
      <Card>
        <p className="text-center py-12 text-gray-500">Select a project to create workplans</p>
      </Card>
    );
  }

  // Step 0: Method Selection
  if (step === 0) {
    return (
      <div className="max-w-5xl mx-auto space-y-4">
        <Card>
          <div className="p-6">
            <h2 className="text-2xl font-bold mb-4">Select Workplan Template Method</h2>
            <p className="text-gray-600 mb-6">
              Choose how you want to generate the workplan template for <strong>{pastProjectName}</strong>
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Import from Previous Workplan */}
              <Card 
                className="p-6 cursor-pointer hover:border-green-500 transition-colors border-2"
                onClick={() => handleMethodSelect('from_past_workplan')}
              >
                <div className="flex flex-col items-center text-center space-y-3">
                  <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-full">
                    <FileText className="h-8 w-8 text-green-600 dark:text-green-400" />
                  </div>
                  <h3 className="text-lg font-semibold">Import Template</h3>
                  <p className="text-sm text-gray-600">
                    Import template from a previous workplan report
                  </p>
                </div>
              </Card>

              {/* Manual Template */}
              <Card 
                className="p-6 cursor-pointer hover:border-purple-500 transition-colors border-2"
                onClick={() => handleMethodSelect('import_template')}
              >
                <div className="flex flex-col items-center text-center space-y-3">
                  <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-full">
                    <FileText className="h-8 w-8 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h3 className="text-lg font-semibold">Manual Template</h3>
                  <p className="text-sm text-gray-600">
                    Use a custom template or start from scratch
                  </p>
                </div>
              </Card>
            </div>

            {onCancel && (
              <div className="mt-6 pt-6 border-t">
                <Button variant="outline" onClick={onCancel} className="w-full">
                  Cancel
                </Button>
              </div>
            )}
          </div>
        </Card>
      </div>
    );
  }

  // Step 3: Job Monitoring
  if (submittedJobId) {
    return (
      <div className="space-y-4">
        <Card>
          <div className="space-y-4">
            <div className="text-center py-2">
              <h3 className="text-lg font-semibold mb-2">Workplan Generation Started</h3>
              <p className="text-gray-600 text-sm mb-2">
                Your workplan is being generated. This may take a few minutes.
              </p>
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-3 py-2 text-xs text-blue-900 dark:text-blue-200">
                <p className="font-medium">You can close this window</p>
                <p className="mt-1">The job will continue running in the background. Check the Work Plan dashboard to view progress.</p>
              </div>
            </div>
            <ReportJobMonitor
              jobId={submittedJobId}
              onComplete={(reportId) => {
                if (onComplete) {
                  onComplete(reportId);
                }
              }}
              onViewReport={(reportId) => {
                window.location.href = `/rfp/workplan-execution?reportId=${reportId}`;
              }}
            />
            {onCancel && (
              <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                <Button variant="outline" onClick={onCancel} className="w-full">
                  Close (Job continues in background)
                </Button>
              </div>
            )}
          </div>
        </Card>
      </div>
    );
  }

  // Template loading/streaming state
  if (isStreaming || templateLoading) {
    const getStageDescription = (stage: string) => {
      switch (stage) {
        case 'retrieval':
        case 'started':
          return 'Searching through past workplans to find relevant templates...';
        case 'analysis':
          return 'Analyzing workplan structures and extracting common patterns...';
        case 'generation':
          return 'Generating comprehensive questions for each section...';
        case 'complete':
          return 'Template extraction complete!';
        default:
          return 'Extracting workplan template...';
      }
    };

    return (
      <Card>
        <div className="py-8">
          <div className="mb-6 p-4 bg-gradient-to-r from-blue-100 via-indigo-100 to-blue-100 dark:from-blue-900/30 dark:via-indigo-900/30 dark:to-blue-900/30 rounded-lg border-2 border-blue-300 dark:border-blue-700 shadow-lg">
            <div className="flex items-center gap-3 mb-3">
              <div className="relative">
                {streamStage === 'retrieval' ? (
                  <Search className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-bounce" />
                ) : streamStage === 'analysis' ? (
                  <Brain className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-pulse" />
                ) : streamStage === 'generation' ? (
                  <Zap className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-spin" />
                ) : (
                  <Loader2 className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-spin" />
                )}
                <span className="absolute -top-1 -right-1 flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500"></span>
                </span>
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-1">
                  {streamMessage || getStageDescription(streamStage)}
                </div>
                <div className="text-xs text-blue-700 dark:text-blue-300">
                  {getStageDescription(streamStage)}
                </div>
              </div>
              {streamElapsed > 0 && (
                <div className="text-right">
                  <div className="text-lg font-bold text-blue-600 dark:text-blue-400">{streamElapsed.toFixed(1)}s</div>
                  <div className="text-xs text-blue-500 dark:text-blue-400">elapsed</div>
                </div>
              )}
              {isStreaming && (
                <button
                  onClick={() => {
                    if (eventSourceRef.current) {
                      eventSourceRef.current.close();
                      eventSourceRef.current = null;
                    }
                    setIsStreaming(false);
                    setTemplateLoading(false);
                    if (streamIntervalRef.current) {
                      clearInterval(streamIntervalRef.current);
                      streamIntervalRef.current = null;
                    }
                  }}
                  className="ml-2 flex items-center gap-1 text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                >
                  <StopCircle className="h-4 w-4" />
                  Cancel
                </button>
              )}
            </div>
            
            {/* Progress bar */}
            <div className="h-2 bg-blue-200 dark:bg-blue-800 rounded-full overflow-hidden shadow-inner">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-300 relative"
                style={{ width: `${streamProgress}%` }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-50 animate-shimmer"></div>
              </div>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  // Step 1: Template Preview and Editing
  const finalTemplate = importedTemplate || template;
  const hasValidTemplate = finalTemplate && 
    finalTemplate.sections && finalTemplate.sections.length > 0 && 
    finalTemplate.default_questions && finalTemplate.default_questions.length > 0;

  if (step === 1 && hasValidTemplate) {
    // Group questions by section
    const questionsBySection = questions.reduce((acc: Record<string, Array<{ section: string; question: string }>>, q) => {
      const section = q.section || 'General';
      if (!acc[section]) acc[section] = [];
      acc[section].push(q);
      return acc;
    }, {});

    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <Card title="Create Workplan" subtitle="Step 1 of 2">
          {/* Template source info */}
          {templateSource && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-3 py-2 flex items-center gap-2 text-xs text-blue-900 dark:text-blue-200 mb-4">
              <AlertCircle className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">
                Template extracted from {templateSource}
                {serviceLine && ` for ${serviceLine}`}
              </span>
            </div>
          )}

          {/* Compressed workplan details */}
          <div className="flex items-center justify-between gap-4 pb-2 border-b border-gray-200 dark:border-gray-700 mb-4">
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">{pastProjectName || 'New Workplan'}</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{clientName || 'Client'}</p>
            </div>
            <div className="w-48 flex-shrink-0">
              <Input
                value={workplanName}
                onChange={(e) => setWorkplanName(e.target.value)}
                placeholder="Workplan name (optional)"
              />
            </div>
          </div>

          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-medium">Workplan Sections</h3>
            <Button variant="outline" size="sm" onClick={() => setStep(2)}>
              Next: Review Questions
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>

          {/* Sections grid - matching ProposalBuilder style */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">
            {finalTemplate.sections.map((section, idx) => {
              const sectionQuestions = questionsBySection[section.name] || [];
              return (
                <div
                  key={idx}
                  className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
                >
                  <div className="flex items-start justify-between mb-1">
                    <span className="text-sm font-medium flex-1 text-gray-900 dark:text-gray-100">{section.name}</span>
                    {sectionQuestions.length > 0 && (
                      <Badge variant="outline" className="text-xs ml-1 flex-shrink-0">
                        {sectionQuestions.length}
                      </Badge>
                    )}
                  </div>
                  {section.description && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                      {section.description}
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Template stats */}
          <div className="grid grid-cols-3 gap-3 pt-3 border-t border-gray-200 dark:border-gray-700">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{finalTemplate.sections.length}</div>
              <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Sections</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">{questions.length}</div>
              <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Questions</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {finalTemplate.sections.length > 0 
                  ? Math.round(questions.length / finalTemplate.sections.length) 
                  : 0}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Avg per Section</div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Step 2: Questions Review and Editing
  if (step === 2) {
    const questionsBySection = questions.reduce((acc: Record<string, Array<{ section: string; question: string }>>, q) => {
      const section = q.section || 'General';
      if (!acc[section]) acc[section] = [];
      acc[section].push(q);
      return acc;
    }, {});

    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <Card title="Create Workplan" subtitle="Step 2 of 2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-medium">Proposal Questions</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {questions.length} questions across {Object.keys(questionsBySection).length} sections
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setStep(1)}>
                <ChevronRight className="h-4 w-4 mr-2 rotate-180" />
                Back
              </Button>
              <Button onClick={handleSubmit} disabled={questions.length === 0}>
                Generate Workplan
              </Button>
            </div>
          </div>

          {/* Questions by Section */}
          <div className="space-y-4">
            {Object.entries(questionsBySection).map(([sectionName, sectionQuestions]) => (
              <div key={sectionName} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <div className="bg-gray-50 dark:bg-gray-800/50 px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    {sectionName} ({sectionQuestions.length} questions)
                  </h4>
                </div>
                <div className="p-4 space-y-3">
                  {sectionQuestions.map((q, qIdx) => {
                    const globalIdx = questions.findIndex(
                      (q2) => q2.section === q.section && q2.question === q.question
                    );
                    return (
                      <div key={qIdx} className="flex items-start gap-2">
                        <div className="flex-1">
                          <textarea
                            value={q.question}
                            onChange={(e) => handleQuestionChange(globalIdx, 'question', e.target.value)}
                            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            rows={3}
                            placeholder="Enter question..."
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveQuestion(globalIdx)}
                          className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 flex-shrink-0"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Add Question */}
            <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
              <Button variant="outline" onClick={handleAddQuestion} className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                Add Custom Question
              </Button>
            </div>
          </div>

          {onCancel && (
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <Button variant="outline" onClick={onCancel} className="w-full">
                Cancel
              </Button>
            </div>
          )}
        </Card>

        {/* Template Import Modal */}
        {showTemplateImport && (
          <WorkplanTemplateImportModal
            isOpen={showTemplateImport}
            onClose={() => setShowTemplateImport(false)}
            pastProjectId={pastProjectId}
            projectId={projectId!}
            onTemplateSelected={handleTemplateImport}
          />
        )}
      </div>
    );
  }

  // Error state
  if (templateError) {
    return (
      <Card>
        <div className="p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Template Extraction Failed</h3>
          <p className="text-gray-600 mb-4">{templateError}</p>
          <div className="flex gap-2 justify-center">
            <Button variant="outline" onClick={() => {
              setTemplateError(null);
              setStep(0);
            }}>
              Try Again
            </Button>
            {onCancel && (
              <Button variant="outline" onClick={onCancel}>
                Cancel
              </Button>
            )}
          </div>
        </div>
      </Card>
    );
  }

  return null;
}
